<template>
  <div class="update-log-page">
    <header class="header">
      <el-button text class="back-btn" aria-label="返回" @click="goBack">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <h1 class="title">版本更新记录</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <el-card
        v-for="(item, index) in updateLogs"
        :key="item.version + '-' + index"
        class="log-card"
        shadow="never"
      >
        <div class="log-header">
          <div class="log-meta">
            <el-tag
              :type="tagType(item.type)"
              effect="light"
              round
              class="version-tag"
            >
              v{{ item.version }}
            </el-tag>
            <el-tag v-if="index === 0" type="danger" effect="plain" size="small">
              最新
            </el-tag>
          </div>
          <span class="log-date">{{ item.date }}</span>
        </div>

        <h3 class="log-title">{{ item.title }}</h3>

        <ul class="log-details">
          <li v-for="(detail, idx) in item.details" :key="idx">
            <el-icon class="detail-icon"><CircleCheckFilled /></el-icon>
            <span>{{ detail }}</span>
          </li>
        </ul>
      </el-card>
    </main>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { CircleCheckFilled } from '@element-plus/icons-vue'

export default {
  name: 'UpdateLog',
  components: { CircleCheckFilled },
  setup() {
    const router = useRouter()

    const updateLogs = ref([
      {
        version: '6.6.01',
        date: '2026-08-04',
        title: '备料库位校验与个人中心优化',
        type: 'major',
        details: [
          '备料库位校验：扫标签后按 wareOut 查询最新出库详情，刷新已扫库位状态',
          '库位校验页展示发运单号、PLS单号（多单号自动拼接）',
          '库位扫码成功/失败区分提示音（失败三声）',
          '摄像头扫码识别灵敏度优化（扩大识别区、提高帧率）',
          '个人中心、系统设置、修改密码页改用 Element Plus 组件',
          '修改密码提示改为 Message，修正接口路径'
        ]
      },
      {
        version: '6.5.10',
        date: '2026-07-18',
        title: '备料标签与打印增强',
        type: 'minor',
        details: [
          '备料标签二维码携带出库单、库位、物料及已扫状态',
          '支持续扫：二维码已扫库位自动高亮，本页补扫后提交',
          '出库打印列表汇总与标签打印体验优化'
        ]
      },
      {
        version: '6.5.02',
        date: '2026-06-20',
        title: '收货与出库体验改进',
        type: 'minor',
        details: [
          '收货流程交互与确认提示优化',
          '出库单据列表筛选与状态展示调整',
          '部分页面适配安全区，避免刘海屏遮挡'
        ]
      },
      {
        version: '6.4.28',
        date: '2026-04-28',
        title: '页面操作优化',
        type: 'major',
        details: ['主要优化了移位和转换功能新增库位扫码']
      },
      {
        version: '6.4.27',
        date: '2026-04-27',
        title: 'bug和页面优化',
        type: 'major',
        details: ['收货界面加入删除功能', '出库显示的bug优化']
      },
      {
        version: '6.4.24',
        date: '2026-04-24',
        title: 'bug和页面优化',
        type: 'major',
        details: ['移位功能bug优化', '收货界面优化']
      },
      {
        version: '3.2.0',
        date: '2026-04-10',
        title: '重大版本更新',
        type: 'major',
        details: [
          '精确化库位管理（收货、上架、PLS扫码入库、出库）',
          '库存查询功能优化',
          '移位、良品转不良品流程改进',
          'PLS备料功能增强'
        ]
      },
      {
        version: '3.1.0',
        date: '2026-04-02',
        title: '功能优化与体验提升',
        type: 'minor',
        details: [
          '新增版本更新记录功能',
          '修复首页底部导航栏高亮问题',
          '优化页面加载速度',
          '改进移动端适配'
        ]
      },
      {
        version: '3.0.0',
        date: '2025-11-15',
        title: '重大版本更新',
        type: 'major',
        details: [
          '全新UI设计改版',
          '新增看板数据分析功能',
          '优化扫码识别速度',
          '提升系统稳定性'
        ]
      },
      {
        version: '2.5.0',
        date: '2025-12-20',
        title: '备料管理模块优化',
        type: 'minor',
        details: [
          '新增PLS备料日志功能',
          '优化备料单流程',
          '修复备料列表显示问题',
          '增加备料统计功能'
        ]
      },
      {
        version: '2.4.0',
        date: '2025-11-10',
        title: '出库管理功能升级',
        type: 'minor',
        details: [
          '新增扫码出库功能',
          '优化出库单创建流程',
          '增加出库记录查询',
          '修复出库数据统计错误'
        ]
      },
      {
        version: '2.3.0',
        date: '2025-10-05',
        title: '库存管理功能优化',
        type: 'minor',
        details: [
          '新增物料库存查询',
          '优化库存盘点流程',
          '改进库存数据展示',
          '增加库存预警功能'
        ]
      },
      {
        version: '2.2.0',
        date: '2025-09-15',
        title: '入库管理功能完善',
        type: 'minor',
        details: [
          '新增PLS扫码入库',
          '优化收货流程',
          '改进上架操作体验',
          '增加入库记录追踪'
        ]
      },
      {
        version: '2.1.0',
        date: '2025-08-20',
        title: '系统稳定性提升',
        type: 'patch',
        details: [
          '修复扫码识别延迟问题',
          '优化网络请求处理',
          '改进数据缓存策略',
          '提升页面加载性能'
        ]
      },
      {
        version: '2.0.0',
        date: '2025-07-15',
        title: '全新版本发布',
        type: 'major',
        details: [
          '移动端WMS系统正式上线',
          '支持扫码入库、出库、盘点等核心功能',
          '响应式设计适配各种移动设备',
          '集成实时数据同步'
        ]
      },
      {
        version: '1.0.0',
        date: '2025-06-01',
        title: '系统初始版本',
        type: 'major',
        details: [
          '基础框架搭建完成',
          '核心业务流程实现',
          '用户认证与权限管理',
          '数据存储与同步机制'
        ]
      }
    ])

    const tagType = (type) => {
      if (type === 'major') return 'danger'
      if (type === 'minor') return 'primary'
      if (type === 'patch') return 'success'
      return 'info'
    }

    const goBack = () => router.back()

    return {
      updateLogs,
      tagType,
      goBack
    }
  }
}
</script>

<style scoped>
.update-log-page {
  min-height: 100vh;
  background: var(--background-color);
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  color: var(--header-text);
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  margin: 0;
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  padding: 12px;
  margin-top: calc(52px + env(safe-area-inset-top));
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}

.log-card {
  margin-bottom: 12px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
}

.log-card :deep(.el-card__body) {
  padding: 14px;
}

.log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
}

.log-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.version-tag {
  font-weight: 600;
}

.log-date {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--text-secondary);
}

.log-title {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.log-details {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.log-details li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
}

.detail-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: var(--primary-color);
  font-size: 14px;
}

.log-details li span {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}
</style>
